import { useMemo } from 'react';
import { useAttendanceHistory } from '../../hooks/useAttendance';
import { formatDuration } from '../../lib/utils';

interface AttendanceSessionRow {
  type: 'work' | 'break';
  clockInAt: string;
  clockOutAt: string | null;
  durationSeconds: number | null;
}

// Placeholder business rules — there's no shift-policy or leave-management
// feature in the backend yet, so "late" and "weekly target" are fixed
// constants rather than per-user configuration. Real "leave" tracking
// (with an approval workflow) doesn't exist at all, so unlike the original
// design mock this card never fabricates a leave count — every number here
// is derived from real attendance_sessions rows.
const LATE_THRESHOLD_MINUTES = 11 * 60; // 11:00 IST
const WEEKLY_TARGET_HOURS = 40;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfWeek(d: Date): Date {
  const copy = new Date(d);
  const dow = copy.getDay(); // 0 = Sunday
  const diff = dow === 0 ? -6 : 1 - dow; // back up to Monday
  copy.setDate(copy.getDate() + diff);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function minutesSinceMidnight(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

function formatHM(d: Date): string {
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

export function AttendanceOverview({ userId }: { userId?: string }) {
  // Stable for the component's lifetime — this is a per-visit snapshot, not
  // a ticking clock, so it deliberately doesn't update if the tab is left
  // open across midnight (matches the granularity of the underlying
  // day/week queries, which also don't refetch on their own for that).
  const now = useMemo(() => new Date(), []);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const weekStart = startOfWeek(now);

  const { data: monthHistory } = useAttendanceHistory({
    userId,
    startDate: dayKey(monthStart),
    endDate: dayKey(now),
    limit: 200,
  });

  const { data: weekHistory } = useAttendanceHistory({
    userId,
    startDate: dayKey(weekStart),
    endDate: dayKey(now),
    limit: 200,
  });

  const stats = useMemo(() => {
    const sessions: AttendanceSessionRow[] = monthHistory?.data ?? [];

    // Group work sessions by the local calendar day of clockInAt.
    const byDay = new Map<string, AttendanceSessionRow[]>();
    for (const s of sessions) {
      const key = dayKey(new Date(s.clockInAt));
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key)!.push(s);
    }

    const lateDays = new Set<string>();
    const firstCheckInMinutes: number[] = [];
    let presentDayCount = 0;

    for (const [key, daySessions] of byDay.entries()) {
      const workSessions = daySessions.filter((s) => s.type === 'work');
      if (workSessions.length === 0) continue;
      presentDayCount++;
      const earliest = workSessions.reduce((min, s) =>
        new Date(s.clockInAt) < new Date(min.clockInAt) ? s : min
      );
      const mins = minutesSinceMidnight(new Date(earliest.clockInAt));
      firstCheckInMinutes.push(mins);
      if (mins > LATE_THRESHOLD_MINUTES) lateDays.add(key);
    }

    const avgCheckInMinutes =
      firstCheckInMinutes.length > 0
        ? Math.round(firstCheckInMinutes.reduce((a, b) => a + b, 0) / firstCheckInMinutes.length)
        : null;
    const avgCheckInLabel =
      avgCheckInMinutes != null
        ? `${String(Math.floor(avgCheckInMinutes / 60)).padStart(2, '0')}:${String(avgCheckInMinutes % 60).padStart(2, '0')}`
        : '--:--';

    // Working days (Mon-Fri) elapsed this month, from the 1st through today.
    let workingDaysElapsed = 0;
    for (let d = 1; d <= now.getDate(); d++) {
      const dow = new Date(now.getFullYear(), now.getMonth(), d).getDay();
      if (dow !== 0 && dow !== 6) workingDaysElapsed++;
    }
    const attendancePercent =
      workingDaysElapsed > 0 ? Math.round((presentDayCount / workingDaysElapsed) * 100) : 0;

    const weekWorkSeconds = ((weekHistory?.data ?? []) as AttendanceSessionRow[])
      .filter((s) => s.type === 'work' && s.durationSeconds)
      .reduce((sum, s) => sum + (s.durationSeconds || 0), 0);

    // Day grid for the full current month.
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const days = Array.from({ length: daysInMonth }, (_, i) => {
      const n = i + 1;
      const date = new Date(now.getFullYear(), now.getMonth(), n);
      const key = dayKey(date);
      const isFuture = n > now.getDate();
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const hasWork = byDay.has(key) && byDay.get(key)!.some((s) => s.type === 'work');
      const isLate = lateDays.has(key);

      if (isFuture) {
        return { n, bg: '#f6f7fb', fg: '#c3c8d6', title: 'Upcoming' };
      }
      if (hasWork && isLate) {
        return { n, bg: '#ffc94d', fg: '#6b4b00', title: 'Late arrival' };
      }
      if (hasWork) {
        return { n, bg: '#2f5cff', fg: '#ffffff', title: 'Present' };
      }
      if (isWeekend) {
        return { n, bg: '#eef0f7', fg: '#9aa2b5', title: 'Weekend' };
      }
      return { n, bg: '#fde2e2', fg: '#b42318', title: 'Absent — no clock-in recorded' };
    });

    // Recent log: last 4 days that actually have a session, most recent first.
    const recentDays = Array.from(byDay.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .slice(0, 4)
      .map(([key, daySessions]) => {
        const workSessions = daySessions.filter((s) => s.type === 'work');
        const totalSeconds = workSessions.reduce((sum, s) => sum + (s.durationSeconds || 0), 0);
        const sorted = [...daySessions].sort(
          (a, b) => new Date(a.clockInAt).getTime() - new Date(b.clockInAt).getTime()
        );
        const first = sorted[0];
        const last = sorted[sorted.length - 1];
        const isOpen = !last.clockOutAt;
        const span = `${formatHM(new Date(first.clockInAt))} - ${
          isOpen ? 'now' : formatHM(new Date(last.clockOutAt as string))
        }`;
        const late = lateDays.has(key);
        const dateObj = new Date(key + 'T00:00:00');
        const dayLabel = dateObj.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' });
        return {
          key,
          day: dayLabel,
          span,
          total: totalSeconds > 0 ? formatDuration(totalSeconds) : '0m',
          status: late ? 'Late' : 'Present',
          dot: late ? '#e0a100' : '#0f9d58',
          chip: late ? '#fff6e0' : '#e8f7ee',
        };
      });

    return {
      presentDayCount,
      workingDaysElapsed,
      attendancePercent,
      avgCheckInLabel,
      weekWorkSeconds,
      lateDayCount: lateDays.size,
      days,
      recentDays,
    };
  }, [monthHistory, weekHistory, now]);

  const kpis = [
    {
      label: 'PRESENT DAYS',
      value: (
        <>
          {stats.presentDayCount}
          <span className="text-[15px] text-[#8b93a7] font-bold">/{stats.workingDaysElapsed}</span>
        </>
      ),
      delta: `${stats.attendancePercent}% attendance`,
      deltaColor: '#0f9d58',
    },
    {
      label: 'AVG CHECK-IN',
      value: stats.avgCheckInLabel,
      delta: `Threshold ${String(Math.floor(LATE_THRESHOLD_MINUTES / 60)).padStart(2, '0')}:${String(LATE_THRESHOLD_MINUTES % 60).padStart(2, '0')}`,
      deltaColor: '#8b93a7',
    },
    {
      label: 'HOURS THIS WEEK',
      value: formatDuration(stats.weekWorkSeconds),
      delta: `of ${WEEKLY_TARGET_HOURS}h target`,
      deltaColor: '#8b93a7',
    },
    {
      label: 'LATE ARRIVALS',
      value: stats.lateDayCount,
      delta: 'This month',
      deltaColor: stats.lateDayCount > 0 ? '#e0a100' : '#8b93a7',
    },
  ];

  return (
    <div className="flex flex-col gap-[22px] min-w-0">
      <div className="grid grid-cols-2 gap-3.5">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="bg-white border border-[#e8eaf2] rounded-[18px] p-[18px]">
            <div className="text-[11.5px] font-bold text-[#8b93a7] tracking-[.4px]">{kpi.label}</div>
            <div className="text-[27px] font-extrabold tracking-[-1px] mt-2 text-[#0d1220]">
              {kpi.value}
            </div>
            <div className="text-xs font-bold mt-1" style={{ color: kpi.deltaColor }}>
              {kpi.delta}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-[#e8eaf2] rounded-[22px] p-6 flex-1 flex flex-col gap-[18px] min-w-0">
        <div className="flex justify-between items-baseline flex-wrap gap-x-3 gap-y-2">
          <div className="text-[15px] font-extrabold whitespace-nowrap text-[#0d1220]">
            Attendance · {MONTH_NAMES[now.getMonth()]}
          </div>
          <div className="flex flex-wrap gap-3.5 text-[11px] font-bold text-[#8b93a7]">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-[3px]" style={{ background: '#2f5cff' }} />
              Present
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-[3px]" style={{ background: '#ffc94d' }} />
              Late
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-[3px]" style={{ background: '#eef0f7' }} />
              Off
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-[3px]" style={{ background: '#fde2e2' }} />
              Absent
            </span>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-[7px]">
          {stats.days.map((d) => (
            <div
              key={d.n}
              title={d.title}
              className="aspect-square rounded-[9px] grid place-items-center text-[11.5px] font-bold"
              style={{ background: d.bg, color: d.fg }}
            >
              {d.n}
            </div>
          ))}
        </div>

        <div className="border-t border-[#eceef5] pt-4 flex flex-col gap-[11px]">
          {stats.recentDays.length === 0 ? (
            <p className="text-sm text-[#8b93a7] text-center py-2">
              No attendance recorded yet this month.
            </p>
          ) : (
            stats.recentDays.map((r) => (
              <div key={r.key} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-[9px] h-[9px] rounded-full shrink-0" style={{ background: r.dot }} />
                  <div className="text-[13px] font-bold text-[#0d1220] whitespace-nowrap">{r.day}</div>
                  <div className="text-xs text-[#8b93a7] font-semibold truncate">{r.span}</div>
                </div>
                <div className="flex items-center gap-2.5 shrink-0">
                  <div className="font-mono text-[12.5px] text-[#3a4256]">{r.total}</div>
                  <div
                    className="text-[11px] font-bold px-[9px] py-1 rounded-full"
                    style={{ color: r.dot, background: r.chip }}
                  >
                    {r.status}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
